<script setup lang="ts">
import { computed, nextTick, ref, watch, type Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEventListener } from '@vueuse/core';
import { ArrowDownIcon, ArrowDownTrayIcon, ArrowUpIcon, BookmarkIcon, PlusIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import NewSavedViewDialog from '@/layouts/frame/NewSavedViewDialog.vue';
import ImportViewDialog from '@/layouts/frame/ImportViewDialog.vue';
import CreateRecordDialog from '@/components/createRecord/CreateRecordDialog.vue';
import AssignNewPlayerDialog from '@/components/createRecord/AssignNewPlayerDialog.vue';
import { CREATE_FORMS, type CreateForm } from '@/components/createRecord/createRecordForms';
import { modelIcon, modelIconTint } from '@/composables/modelIcons';
import { isGateOpen } from '@/navigation/gates';
import { isEntityListRoute } from '@/navigation/activeArea';
import { PAGE_ICONS } from '@/navigation/navIcons';
import { useAuthStore } from '@/stores/authStore';
import {
    actionLabelFor,
    isTypingTarget,
    unavailableReason,
    visibleCreateSections,
    type CreateOption,
    type CreateOptionId,
} from '@/layouts/frame/createOptions';

/**
 * The "+" at the top of the icon strip, and the Create New dialog it opens,
 * laid out like Macro's: the options on the left, the highlighted one described
 * on the right, and a "Create" button. Pressing C anywhere outside a text box
 * opens it too. Inside it, each option's letter creates that option at once.
 */

const route = useRoute();
const router = useRouter();
const onEntityList = computed(() => isEntityListRoute(route));

const open = ref(false);
const newViewOpen = ref(false);
const importOpen = ref(false);

const authStore = useAuthStore();
const sections = computed(() => visibleCreateSections(isGateOpen, Boolean(authStore.userData?.clients_only)));
const options = computed(() => sections.value.flatMap((section) => section.options));
const highlightedId = ref<CreateOptionId>('saved-view');
const highlighted = computed(() => options.value.find((option) => option.id === highlightedId.value) ?? options.value[0]);
const highlightedReason = computed(() => (highlighted.value ? unavailableReason(highlighted.value, onEntityList.value) : null));

const FIXED_ICONS: Partial<Record<CreateOptionId, { icon: Component; tint: string }>> = {
    'saved-view': { icon: BookmarkIcon, tint: 'text-lime-600 dark:text-lime-400' },
    'import-view': { icon: ArrowDownTrayIcon, tint: 'text-sky-600 dark:text-sky-400' },
    'ddex-configuration': { icon: PAGE_ICONS['ddex-configurations'], tint: 'text-violet-600 dark:text-violet-400' },
};

interface OptionIcon {
    icon: Component;
    /** The row icon's colour class. */
    tint: string;
    /** The large tile behind the icon, whose `bg-current` takes the icon's colour. */
    tile: { class: string; style?: Record<string, string> };
}

/**
 * One choice for both the row icon and the preview tile, so they always agree.
 * A record type's icon draws in its own colour, so its row needs no tint; the
 * tile gets the same colour from the icon map.
 */
function iconFor(option: CreateOption): OptionIcon {
    const fixed = FIXED_ICONS[option.id];
    if (fixed) return { ...fixed, tile: { class: fixed.tint } };
    const recordIcon = modelIcon(option.entityId);
    const recordTint = modelIconTint(option.entityId);
    if (recordIcon && recordTint) return { icon: recordIcon, tint: '', tile: recordTint };
    return { icon: PlusIcon, tint: 'text-foreground', tile: { class: 'text-foreground' } };
}

const recordForm = ref<CreateForm | null>(null);
const recordFormOpen = ref(false);
const newPlayerOpen = ref(false);

watch(open, (isOpen) => {
    if (isOpen) {
        // Start on the first option that can be made here.
        highlightedId.value = (options.value.find((option) => !unavailableReason(option, onEntityList.value)) ?? options.value[0]).id;
    }
});

function create(option: CreateOption | undefined) {
    if (!option || unavailableReason(option, onEntityList.value)) {
        return;
    }
    open.value = false;
    switch (option.id) {
        case 'saved-view':
            newViewOpen.value = true;
            return;
        case 'import-view':
            importOpen.value = true;
            return;
        case 'ddex-configuration':
            void router.push({ name: 'ddex-configurations' });
            return;
        case 'new-player':
            newPlayerOpen.value = true;
            return;
    }
    if (option.entityId && CREATE_FORMS[option.entityId]) {
        recordForm.value = CREATE_FORMS[option.entityId];
        recordFormOpen.value = true;
    }
}

/**
 * The dialog would focus its first button, the saved-view row, and focusing a
 * row highlights it. So on Home, where a saved view cannot be made, it opened
 * on the wrong row. Focus the highlighted row instead.
 */
function focusHighlighted(event: Event) {
    event.preventDefault();
    void nextTick(() => {
        document.querySelector<HTMLElement>(`[data-testid="create-option-${highlightedId.value}"]`)?.focus();
    });
}

function moveHighlight(step: number) {
    const list = options.value;
    const index = list.findIndex((option) => option.id === highlightedId.value);
    highlightedId.value = list[(index + step + list.length) % list.length].id;
}

function onDialogKeydown(event: KeyboardEvent) {
    if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
        return;
    }
    if (event.key === 'Enter') {
        event.preventDefault();
        create(highlighted.value);
        return;
    }
    const byLetter = options.value.find((option) => option.shortcut === event.key.toLowerCase());
    if (byLetter) {
        event.preventDefault();
        highlightedId.value = byLetter.id;
        create(byLetter);
    }
}

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (open.value || event.key.toLowerCase() !== 'c' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
    }
    // Stand down while a dialog, menu or listbox is open: there C may pick an
    // item by its first letter, and Create New would open behind it.
    if (isTypingTarget(event.target) || event.defaultPrevented || document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) {
        return;
    }
    event.preventDefault();
    open.value = true;
});
</script>

<template>
  <button
    type="button"
    aria-label="Create"
    title="Create (C)"
    data-testid="create-button"
    class="flex size-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
    :class="open && 'bg-sidebar-accent text-foreground'"
    @click="open = true"
  >
    <PlusIcon class="size-5" aria-hidden="true" />
  </button>

  <Dialog v-model:open="open">
    <DialogContent
      hide-close-button
      data-testid="create-dialog"
      class="top-[10vh] max-w-[calc(100vw-1rem)] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-[54rem]"
      @keydown="onDialogKeydown"
      @open-auto-focus="focusHighlighted"
    >
      <div class="flex items-center gap-3 border-b border-border/50 px-5 py-3">
        <PlusIcon class="size-4 text-muted-foreground" aria-hidden="true" />
        <DialogTitle class="text-[15px] font-normal text-secondary-foreground/80">Create New</DialogTitle>
        <DialogDescription class="sr-only">Pick what to create. Each option's letter creates it straight away.</DialogDescription>
      </div>

      <!-- On a phone the list fills the dialog and a tap creates; the detail pane needs a wider screen. -->
      <div class="flex md:min-h-80">
        <div role="listbox" aria-label="Create options" class="max-h-[70vh] w-full shrink-0 overflow-y-auto p-2 md:max-h-[60vh] md:w-64 md:border-r md:border-border/50">
          <template v-for="section in sections" :key="section.label">
            <div class="px-2.5 pt-2 pb-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">{{ section.label }}</div>
            <button
              v-for="option in section.options"
              :key="option.id"
              type="button"
              role="option"
              :aria-selected="option.id === highlighted?.id"
              :data-testid="`create-option-${option.id}`"
              :aria-disabled="Boolean(unavailableReason(option, onEntityList))"
              :title="unavailableReason(option, onEntityList) ?? undefined"
              class="flex min-h-9 w-full items-center gap-2.5 rounded-md px-2.5 py-1 text-left text-secondary-foreground/80 outline-none aria-disabled:opacity-50 aria-selected:bg-foreground/[0.06] aria-selected:text-foreground"
              @mouseenter="highlightedId = option.id"
              @focus="highlightedId = option.id"
              @click="create(option)"
            >
              <component :is="iconFor(option).icon" :class="['size-4 shrink-0', iconFor(option).tint]" aria-hidden="true" />
              <span class="flex min-w-0 flex-1 flex-col">
                <span class="truncate text-sm font-medium">{{ option.label }}</span>
                <!-- A phone has no detail pane to say why, so the row says it. -->
                <span v-if="unavailableReason(option, onEntityList)" class="text-xs text-muted-foreground md:hidden">{{ unavailableReason(option, onEntityList) }}</span>
              </span>
              <kbd class="hidden rounded-md border border-foreground/12 px-1.5 py-px font-sans text-[10px] text-muted-foreground uppercase md:inline">{{ option.shortcut }}</kbd>
            </button>
          </template>
        </div>

        <div v-if="highlighted" class="hidden min-w-0 flex-1 flex-col p-6 md:flex" data-testid="create-detail">
          <div :class="['flex size-16 items-center justify-center rounded-2xl bg-current/15', iconFor(highlighted).tile.class]" :style="iconFor(highlighted).tile.style">
            <component :is="iconFor(highlighted).icon" class="size-8" aria-hidden="true" />
          </div>
          <h3 class="mt-5 text-xl font-semibold">{{ highlighted.label }}</h3>
          <p class="mt-1.5 text-sm text-secondary-foreground/80">{{ highlighted.description }}</p>
          <p class="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            Shortcut
            <kbd class="rounded-md border border-foreground/12 px-1.5 py-px font-sans">C</kbd>
            then
            <kbd class="rounded-md border border-foreground/12 px-1.5 py-px font-sans uppercase">{{ highlighted.shortcut }}</kbd>
          </p>
          <p v-if="highlightedReason" class="mt-3 text-sm text-muted-foreground" data-testid="create-unavailable">{{ highlightedReason }}</p>
          <div class="mt-auto flex justify-end pt-6">
            <button
              type="button"
              data-testid="create-confirm"
              :disabled="Boolean(highlightedReason)"
              class="inline-flex h-9 items-center gap-2 rounded-full border border-foreground/10 bg-popover px-3.5 text-sm font-semibold text-secondary-foreground hover:bg-foreground/[0.06] disabled:cursor-default disabled:opacity-40"
              @click="create(highlighted)"
            >
              {{ actionLabelFor(highlighted) }}
              <kbd class="rounded-md border border-foreground/12 px-1 font-sans text-[10px]">↵</kbd>
            </button>
          </div>
        </div>
      </div>

      <div class="hidden items-center gap-5 border-t border-border/50 px-5 py-2.5 text-xs text-muted-foreground md:flex">
        <span class="flex items-center gap-1.5">
          <kbd class="rounded-md border border-foreground/12 px-1.5 py-px"><ArrowUpIcon class="size-3" /></kbd>
          <kbd class="rounded-md border border-foreground/12 px-1.5 py-px"><ArrowDownIcon class="size-3" /></kbd>
          Navigate
        </span>
        <span class="flex items-center gap-1.5">
          <kbd class="rounded-md border border-foreground/12 px-1.5 py-px font-sans">↵</kbd>
          Create
        </span>
      </div>
    </DialogContent>
  </Dialog>

  <NewSavedViewDialog v-if="onEntityList" v-model:open="newViewOpen" />
  <ImportViewDialog v-model:open="importOpen" />
  <CreateRecordDialog v-if="recordForm" v-model:open="recordFormOpen" :form="recordForm" />
  <AssignNewPlayerDialog v-model:open="newPlayerOpen" />
</template>
